import { useState, useEffect, useCallback } from 'react';
import { getJobCostSummary } from '../api/jobCosts';
import type { CostSummary } from '../types/jobCost';

/**
 * Hook for fetching job cost summary
 */
export function useJobCostSummary(jobId: number | undefined, isForecast?: boolean) {
  const [data, setData] = useState<CostSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!jobId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const summary = await getJobCostSummary(jobId, isForecast);
      setData(summary);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch cost summary');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, isForecast]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSummary,
  };
}
